import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Card, Alert } from "react-bootstrap";
import { FaCog, FaInfoCircle, FaFileAlt, FaEye } from "react-icons/fa";

const SmartPDFSettings = ({ show, onHide, settings, onChange }) => {
  const [localSettings, setLocalSettings] = useState({
    question_spacing: 0.5,
    page_threshold: 80,
    answer_spacing: 0.5,
    answer_page_threshold: 80,
    enable_continuation_headers: true,
    show_source_badges: true,
    ...settings,
  });

  // Update local settings when props change
  useEffect(() => {
    setLocalSettings({
      question_spacing: 0.5,
      page_threshold: 80,
      answer_spacing: 0.5,
      answer_page_threshold: 80,
      enable_continuation_headers: true,
      show_source_badges: true,
      ...settings,
    });
  }, [settings, show]);

  const handleSettingChange = (key, value) => {
    setLocalSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetToDefaults = () => {
    setLocalSettings({
      question_spacing: 0.5,
      page_threshold: 80,
      answer_spacing: 0.5,
      answer_page_threshold: 80,
      enable_continuation_headers: true,
      show_source_badges: true,
    });
  };

  const applySettings = () => {
    // Save to localStorage for persistence
    localStorage.setItem(
      "lioncity_smart_pdf_settings",
      JSON.stringify(localSettings)
    );

    // Apply to parent component
    onChange(localSettings);
    onHide();
  };

  const formatSpacing = (value) => {
    return `${value}"`;
  };

  const formatThreshold = (value) => {
    return `${value}%`;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <FaCog className="text-primary" />
          Smart PDF Settings
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert variant="info" className="mb-4">
          <FaInfoCircle className="me-2" />
          Customize your Smart PDF layout preferences. These settings will be
          saved for future use.
        </Alert>

        {/* Questions Section */}
        <Card className="mb-4">
          <Card.Header className="bg-light">
            <h6 className="mb-0 d-flex align-items-center gap-2">
              <FaFileAlt className="text-primary" />
              Questions Layout
            </h6>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Question Spacing:{" "}
                    <strong>
                      {formatSpacing(localSettings.question_spacing)}
                    </strong>
                  </Form.Label>
                  <Form.Range
                    min={0.25}
                    max={1.0}
                    step={0.05}
                    value={localSettings.question_spacing}
                    onChange={(e) =>
                      handleSettingChange(
                        "question_spacing",
                        parseFloat(e.target.value)
                      )
                    }
                  />
                  <Form.Text className="text-muted">
                    Space between questions (0.25" - 1.0")
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Page Break Threshold:{" "}
                    <strong>
                      {formatThreshold(localSettings.page_threshold)}
                    </strong>
                  </Form.Label>
                  <Form.Range
                    min={70}
                    max={95}
                    step={1}
                    value={localSettings.page_threshold}
                    onChange={(e) =>
                      handleSettingChange(
                        "page_threshold",
                        parseInt(e.target.value)
                      )
                    }
                  />
                  <Form.Text className="text-muted">
                    Page usage before forcing new page (70% - 95%)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Answers Section */}
        <Card className="mb-4">
          <Card.Header className="bg-light">
            <h6 className="mb-0 d-flex align-items-center gap-2">
              <FaEye className="text-success" />
              Answer Key Layout
            </h6>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Answer Spacing:{" "}
                    <strong>
                      {formatSpacing(localSettings.answer_spacing)}
                    </strong>
                  </Form.Label>
                  <Form.Range
                    min={0.25}
                    max={1.0}
                    step={0.05}
                    value={localSettings.answer_spacing}
                    onChange={(e) =>
                      handleSettingChange(
                        "answer_spacing",
                        parseFloat(e.target.value)
                      )
                    }
                  />
                  <Form.Text className="text-muted">
                    Space between answers (0.25" - 1.0")
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Answer Page Threshold:{" "}
                    <strong>
                      {formatThreshold(localSettings.answer_page_threshold)}
                    </strong>
                  </Form.Label>
                  <Form.Range
                    min={70}
                    max={95}
                    step={1}
                    value={localSettings.answer_page_threshold}
                    onChange={(e) =>
                      handleSettingChange(
                        "answer_page_threshold",
                        parseInt(e.target.value)
                      )
                    }
                  />
                  <Form.Text className="text-muted">
                    Page usage before forcing new page for answers (70% - 95%)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Display Options */}
        <Card className="mb-4">
          <Card.Header className="bg-light">
            <h6 className="mb-0">Display Options</h6>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Check
                  type="switch"
                  id="continuation-headers"
                  label="Enable Continuation Headers"
                  checked={localSettings.enable_continuation_headers}
                  onChange={(e) =>
                    handleSettingChange(
                      "enable_continuation_headers",
                      e.target.checked
                    )
                  }
                  className="mb-3"
                />
                <Form.Text className="text-muted d-block">
                  Show headers when questions continue across pages
                </Form.Text>
              </Col>

              <Col md={6}>
                <Form.Check
                  type="switch"
                  id="source-badges"
                  label="Show Source Badges"
                  checked={localSettings.show_source_badges}
                  onChange={(e) =>
                    handleSettingChange("show_source_badges", e.target.checked)
                  }
                  className="mb-3"
                />
                <Form.Text className="text-muted d-block">
                  Display "My Question" badges for user-created questions
                </Form.Text>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Preview Info */}
        <Alert variant="light" className="border">
          <h6 className="mb-2">Settings Preview</h6>
          <ul className="mb-0 small">
            <li>
              Question spacing: {formatSpacing(localSettings.question_spacing)}
            </li>
            <li>
              Answer spacing: {formatSpacing(localSettings.answer_spacing)}
            </li>
            <li>
              Page break at {formatThreshold(localSettings.page_threshold)}{" "}
              usage
            </li>
            <li>
              Source badges:{" "}
              {localSettings.show_source_badges ? "Enabled" : "Disabled"}
            </li>
            <li>
              Continuation headers:{" "}
              {localSettings.enable_continuation_headers
                ? "Enabled"
                : "Disabled"}
            </li>
          </ul>
        </Alert>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={resetToDefaults}>
          Reset to Defaults
        </Button>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={applySettings}>
          Apply Settings
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SmartPDFSettings;
